import { cn } from '@/lib/utils';
import { AlertTriangle, RefreshCw, type LucideIcon } from 'lucide-react';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { logger } from '@/config/logging';
import { describeAuthState } from '@/lib/authDiagnostics';
import { MAX_WIDTH_CLASS } from './Panel';

// #3577: what the user saw ("Something went wrong … Error"), with the auth
// state. Routed to the persisted auth log, see config/logging.ts.
const panelLogger = logger.getChild(['app', 'errorPanel']);

export interface ErrorPanelProps {
  error: Error;
  resetErrorBoundary: () => void;
  title?: string;
  icon?: LucideIcon;
  path?: string;
  width?: 'collapsed' | 'compact' | 'wide' | 'full';
  maxPanelWidth?: 'sm' | 'md' | 'lg';
  hidden?: boolean;
}

export const ErrorPanel: React.FC<ErrorPanelProps> = ({
  error,
  resetErrorBoundary,
  title,
  icon: Icon,
  path,
  width = 'wide',
  maxPanelWidth,
  hidden = false,
}) => {
  useEffect(() => {
    const status = (error as { status?: number }).status;
    panelLogger.warn('Error panel shown: {message} ({path})', {
      message: error.message || error.toString(),
      name: error.name,
      status: status ?? null,
      url: (error as { url?: string }).url ?? null,
      path,
      title,
      onLine: navigator.onLine,
      visibility: document.visibilityState,
      msSinceLoad: Math.round(performance.now()),
      auth: describeAuthState(),
      stack: error.stack?.split('\n').slice(0, 4).join(' | '),
    });
  }, [error, path, title]);

  return (
    <section
      className={cn(
        '@container',
        'block shrink-0',
        hidden && 'hidden',
        'sticky top-2',
        'md:max-h-[calc(100vh-var(--spacing)*14)]',
        'md:min-h-[calc(100vh-var(--spacing)*14)]',
        'md:overflow-y-auto',
        width === 'full' && 'grow-[6] md:w-lg',
        width === 'wide' && 'max-w-6xl grow md:w-lg',
        width === 'compact' && 'grow md:w-md md:min-w-sm',
        (width === 'full' || width === 'compact') &&
          maxPanelWidth &&
          MAX_WIDTH_CLASS[maxPanelWidth],
        width === 'collapsed' && 'w-0! md:w-0! md:pl-0',
        'bg-card md:rounded-lg',
        'shadow-[0_4px_8px_rgba(0,0,0,0.2)]',
      )}
    >
      <div
        className={cn(
          'md:min-h-[calc(100vh-var(--spacing)*15)]',
          'mb-1 block shrink-0',
          width === 'wide' && 'max-w-6xl grow md:w-lg',
          width === 'collapsed' && 'origin-left scale-100 opacity-0',
        )}
      >
        {/* Panel Header */}
        <div
          className={cn(
            `flex items-center justify-between border-b border-red-200 bg-red-50 p-1 md:p-2
            dark:border-red-900 dark:bg-red-950/40`,
            'sticky top-0 z-10 md:rounded-t-lg',
          )}
        >
          <div className="m-2 flex min-w-0 items-center gap-2">
            {Icon ? (
              <Icon className="h-4 w-4 shrink-0 text-red-500" />
            ) : (
              <AlertTriangle className="h-4 w-4 shrink-0 text-red-500" />
            )}
            <h3 className="truncate text-sm leading-none font-medium text-red-700 dark:text-red-300">
              {title || 'Panel Error'}
            </h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={resetErrorBoundary}
            className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:text-red-400
              dark:hover:bg-red-950/60 dark:hover:text-red-300"
          >
            <RefreshCw className="mr-1 h-3 w-3" />
            Retry
          </Button>
        </div>

        {/* Error Content */}
        <div className="p-4">
          <div
            className="rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-900
              dark:bg-red-950/40"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Something went wrong
                </h4>
                <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                  {error.message || error.toString()}
                </p>
                {path && (
                  <p className="mt-2 font-mono text-xs text-red-500 dark:text-red-400">
                    Path: {path}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
